/**
 * Cron Parser View
 * Interactive cron expression parsing and explanation
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService } from '../../../services/utils.service.js';

export const CronView: React.FC = () => {
    const [expression, setExpression] = useState('');
    const [result, setResult] = useState<{ description: string; nextRuns: string[] } | null>(null);
    const [error, setError] = useState('');
    const [focusedField, setFocusedField] = useState<'expression' | 'parse'>('expression');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    useInput((input, key) => {
        if (key.tab) {
            setFocusedField(focusedField === 'expression' ? 'parse' : 'expression');
        } else if (key.return) {
            // Parse on Enter from any field
            handleParse();
        } else if (input === 'c' && result) {
            handleCopy();
        }
    });

    const handleParse = () => {
        if (!expression) {
            setError('Please enter a cron expression');
            return;
        }

        try {
            setError('');
            const parsed = service.parseCron(expression);
            setResult(parsed);
            setCopied(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to parse cron expression');
            setResult(null);
        }
    };

    const handleCopy = async () => {
        if (!result) return;
        try {
            const { default: clipboardy } = await import('clipboardy');
            const copyText = `${result.description}\n\nNext runs:\n${result.nextRuns.join('\n')}`;
            await clipboardy.write(copyText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    ⏰ Cron Expression Parser
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'expression' ? 'green' : undefined}>
                            {focusedField === 'expression' ? '▶ ' : '  '}Cron Expression:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={50}>
                        {focusedField === 'expression' ? (
                            <TextInput
                                value={expression}
                                onChange={setExpression}
                                placeholder="0 5 * * * (5-field or 6-field)"
                            />
                        ) : (
                            <Text dimColor={!expression}>{expression || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginTop={1} marginBottom={1}>
                    <Text dimColor>
                        Examples: "0 5 * * *" (daily at 5am), "*/15 * * * *" (every 15 min)
                    </Text>
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'parse' ? 'green' : undefined}
                        color={focusedField === 'parse' ? 'black' : 'green'}
                    >
                        {focusedField === 'parse' ? '▶ ' : '  '}
                        [ Parse Expression ]
                    </Text>
                </Box>
            </Box>

            {error && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="red"
                    paddingX={2}
                    paddingY={1}
                    marginBottom={1}
                >
                    <Text color="red">✗ Error: {error}</Text>
                </Box>
            )}

            {result && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text bold color="yellow">Expression:</Text>
                        <Text>{expression}</Text>
                        <Text> </Text>

                        <Text bold color="yellow">Meaning:</Text>
                        <Text>{result.description}</Text>
                        <Text> </Text>

                        <Text bold color="yellow">Next 5 executions:</Text>
                        {result.nextRuns.map((run, index) => (
                            <Text key={index}>  {index + 1}. {run}</Text>
                        ))}
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | Enter: Parse | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
